import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Crown, Zap, Coins, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPlans, startCheckout, switchPlan, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { getPlans().then(setPlans).catch(() => {}); }, []);

  const buy = async (lookup_key, { isSwitch = false } = {}) => {
    if (!user) { navigate("/login", { state: { from: "/pricing" } }); return; }
    setBusy(lookup_key);
    try {
      if (isSwitch) {
        const r = await switchPlan(lookup_key);
        if (r?.ok) {
          await refresh();
          toast.success(`You're now on ${r.plan === "creator_plus" ? "Creator Plus" : "Pro"} — the difference was prorated on your card`);
          setBusy("");
          return;
        }
        // no active subscription on file → fall through to a normal checkout
      }
      const { checkout_url } = await startCheckout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(errText(e));
      setBusy("");
    }
  };

  const subs = plans.filter((p) => p.kind === "subscription");
  const packs = plans.filter((p) => p.kind === "one_time");
  const isPaid = ["pro", "creator_plus"].includes(user?.plan) || user?.role === "admin";
  const currentPlan = user?.role === "admin" ? "creator_plus" : user?.plan;

  const PLAN_UI = {
    pro: {
      key: "pro", tag: "MOST POPULAR", border: "border-[#FF453A]", bg: "bg-[#15100F]", accent: "text-[#FF6A5E]", check: "text-[#FF6A5E]",
      features: [<><strong>1 video every day</strong> (≈30 a month)</>, "Priority render queue (skip the line)", "Buy credit packs for extra videos anytime", "Cancel anytime"],
    },
    creator_plus: {
      key: "creator_plus", tag: "BEST FOR DAILY POSTING", border: "border-amber-400/70", bg: "bg-[#161309]", accent: "text-amber-300", check: "text-amber-300",
      features: [<><strong>2 videos every day</strong> (≈60 a month)</>, "Daily autopilot + Remix for both slots", "Priority render queue (skip the line)", "Buy credit packs for extra videos anytime", "Cancel anytime"],
    },
  };

  const renderSub = (p) => {
    const ui = PLAN_UI[p.plan] || PLAN_UI.pro;
    const savePct = p.compare_at ? Math.round((1 - p.amount / p.compare_at) * 100) : 0;
    const isCurrent = currentPlan === p.plan;
    const onPaid = ["pro", "creator_plus"].includes(user?.plan);
    const isUpgrade = p.plan === "creator_plus" && currentPlan === "pro";
    const isDowngrade = p.plan === "pro" && currentPlan === "creator_plus" && user?.role !== "admin";
    return (
      <div key={p.lookup_key} data-testid={`plan-${ui.key}`} className={`relative rounded-3xl border-2 ${ui.border} ${ui.bg} p-8`}>
        <span className={`absolute -top-3 left-8 rounded-full px-3 py-1 text-xs font-bold text-white ${p.plan === "creator_plus" ? "bg-amber-500" : "bg-[#FF453A]"}`}>{ui.tag}</span>
        {savePct > 0 && (
          <span data-testid={`${ui.key}-discount-badge`} className="absolute -top-3 right-8 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white">SAVE {savePct}%</span>
        )}
        <div className={`flex items-center gap-2 ${ui.accent}`}><Crown className="h-5 w-5" /><span className="font-semibold">{p.label}</span></div>
        <div className="mt-4 flex items-end gap-2">
          {p.compare_at && (
            <span data-testid={`${ui.key}-compare-at`} className="font-display font-bold text-2xl text-slate-500 line-through mb-1">${p.compare_at.toFixed(2)}</span>
          )}
          <p className="font-display font-extrabold text-4xl" data-testid={`${ui.key}-price`}>${p.amount.toFixed(2)}<span className="text-lg text-slate-500 font-body">/mo</span></p>
        </div>
        {savePct > 0 && <p className="text-xs text-emerald-400 font-medium mt-1">Limited-time {savePct}% launch discount</p>}
        <ul className="mt-6 space-y-2.5 text-sm text-slate-200">
          {ui.features.map((f, i) => (
            <li key={i} className="flex gap-2"><Check className={`h-4 w-4 ${ui.check} shrink-0`} /> <span>{f}</span></li>
          ))}
        </ul>
        {isCurrent ? (
          <Button disabled className="mt-8 w-full rounded-full bg-white/10 text-white" data-testid={`plan-${ui.key}-current`}>You're on {p.label} 🎉</Button>
        ) : (
          <>
            <Button onClick={() => buy(p.lookup_key, { isSwitch: onPaid })} disabled={busy === p.lookup_key}
              className={`mt-8 w-full rounded-full text-white font-semibold ${p.plan === "creator_plus" ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-[#FF453A] hover:bg-[#FF5D54]"}`}
              data-testid={`plan-${ui.key}-btn`}>
              {busy === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : isUpgrade ? "Upgrade to Creator Plus" : (isDowngrade || onPaid) ? `Switch to ${p.label}` : `Upgrade to ${p.label}`}
            </Button>
            {onPaid && !isCurrent && (
              <p className="mt-2 text-center text-xs text-slate-500" data-testid={`plan-${ui.key}-prorate-note`}>
                Switches instantly — you only pay (or get credited) the prorated difference for the rest of this month.
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <div className="text-center">
        <p className="eyebrow">Plans</p>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2">Create more, faster.</h1>
        <p className="text-slate-400 mt-3 max-w-xl mx-auto">Try it free with 1 video a week. Go Pro for a fresh ad every day, or Creator Plus for two — both with priority rendering and credit top-ups whenever you need more.</p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {/* Free Trial Run */}
        <div data-testid="plan-free" className="rounded-3xl border border-white/10 bg-[#11141E] p-8">
          <div className="flex items-center gap-2 text-slate-300"><Zap className="h-5 w-5" /><span className="font-semibold">Free Trial Run</span></div>
          <p className="mt-4 font-display font-extrabold text-4xl">$0<span className="text-lg text-slate-500 font-body">/wk</span></p>
          <ul className="mt-6 space-y-2.5 text-sm text-slate-300">
            <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> <span><strong>1 free video per week</strong> — try the platform</span></li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> All visual styles & captions</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> Image reference uploads</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-400 shrink-0" /> Standard render queue</li>
          </ul>
          <Button disabled className="mt-8 w-full rounded-full bg-white/5 text-slate-400 border border-white/10" data-testid="plan-free-btn">
            {!isPaid ? "Current plan" : "Trial"}
          </Button>
        </div>

        {subs.map(renderSub)}
      </div>

      {/* Credit packs — Pro only */}
      <div className="mt-14">
        <div className="flex items-center gap-2 justify-center text-slate-300">
          <Coins className="h-5 w-5 text-amber-400" />
          <h2 className="font-display font-bold text-xl">Top up with one-time credits</h2>
        </div>
        <p className="text-center text-sm text-slate-500 mt-1">1 credit = 1 extra video beyond your daily allowance. Credits never expire. <strong className="text-slate-400">Pro &amp; Creator Plus members only.</strong></p>

        {!isPaid ? (
          <div data-testid="packs-locked" className="mt-6 rounded-2xl border border-white/10 bg-[#11141E] p-8 text-center max-w-lg mx-auto">
            <Lock className="h-6 w-6 text-slate-500 mx-auto" />
            <p className="mt-3 text-sm text-slate-300">Credit packs unlock once you're on Pro or Creator Plus.</p>
            <Button onClick={() => buy("pro_monthly")} className="mt-4 rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white font-semibold px-6" data-testid="packs-upgrade-btn">
              Upgrade to Pro to unlock credits
            </Button>
          </div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {packs.map((p) => (
              <div key={p.lookup_key} data-testid={`pack-${p.lookup_key}`} className="rounded-2xl border border-white/10 bg-[#11141E] p-6 text-center">
                <p className="font-display font-extrabold text-2xl">{p.label}</p>
                <p className="text-xs text-slate-400 mt-1">{p.blurb}</p>
                <p className="mt-4 font-display font-bold text-3xl">${p.amount}</p>
                <Button onClick={() => buy(p.lookup_key)} disabled={busy === p.lookup_key} variant="outline" className="mt-4 w-full rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white" data-testid={`pack-${p.lookup_key}-btn`}>
                  {busy === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy credits"}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-center text-xs text-slate-500 mt-8">Test mode — use card 4242 4242 4242 4242, any future expiry & CVC.</p>
    </div>
  );
}
